import React, { useState, useEffect, useRef } from "react";
import {
  ContentLayout,
  Container,
  Header,
  Box,
  Spinner,
  Alert,
  SpaceBetween,
  Button,
} from "@cloudscape-design/components";
import { useTranslation } from "react-i18next";
import { useAlert } from "../components/Alert";
import { getQuickSightEmbedUrl } from "../api/api";
import * as QuickSightEmbedding from "amazon-quicksight-embedding-sdk";

const Insights = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showError } = useAlert();
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  const containerRef = useRef(null);
  const dashboardWrapperRef = useRef(null);

  const toggleFullscreen = () => {
    if (!dashboardWrapperRef.current) return;
    
    if (!document.fullscreenElement) {
      dashboardWrapperRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error al intentar mostrar pantalla completa: ${err.message}`);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error(`Error al salir de pantalla completa: ${err.message}`);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenActive = !!document.fullscreenElement;
      setIsFullscreen(fullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (dashboard) {
      const timeoutId = setTimeout(() => {
        dashboard.resize();
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen, dashboard]);

  useEffect(() => {
    const loadQuickSightDashboard = async () => {
      try {
        setError(null);
        
        const response = await getQuickSightEmbedUrl();
        if (!response || !response.embeddedUrl) {
          throw new Error(t("dashboard.loadError"));
        }
        
        if (!containerRef.current) {
          throw new Error("El contenedor para el dashboard no se encontro.");
        }
        
        const embeddingContext = await QuickSightEmbedding.createEmbeddingContext();
        const dashboardInstance = embeddingContext.embedDashboard({
          url: response.embeddedUrl,
          container: containerRef.current,
          height: "100%",
          width: "100%", 
          resizeHeightOnSizeChangedEvent: true
        });
        
        setDashboard(dashboardInstance);
      } catch (error) {
        console.error("Error al cargar el dashboard:", error);
        setError(error.message);
        showError(t("dashboard.loadError"));
      } finally {
        setLoading(false);
      }
    };

    if (containerRef.current) {
      loadQuickSightDashboard();
    }
  }, [showError, t]);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t("dashboard.description")}
          actions={
            <Button 
              iconName={isFullscreen ? "view-normal" : "expand"} 
              onClick={toggleFullscreen}
              disabled={loading || !!error}
            >
              {isFullscreen ? t("dashboard.exitFullscreen") : t("dashboard.fullscreen")}
            </Button>
          }
        >
          {t("dashboard.title")}
        </Header>
      }
    >
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Container>
        <SpaceBetween size="l">
          {loading && (
            <Box textAlign="center" padding={{ bottom: "l" }}>
              <Spinner size="large" />
              <Box padding={{ top: "s" }} color="text-status-info">
                {t("dashboard.loading")}
              </Box>
            </Box>
          )}
          <div 
            ref={dashboardWrapperRef}
            style={{
              width: "100%",
              height: isFullscreen ? "100vh" : "auto",
              overflow: "auto",
            }}
          >
            <Box padding={isFullscreen ? "none" : "s"}>
              <div
                ref={containerRef}
                id="quicksight-container"
                style={{
                  height: isFullscreen ? "calc(100vh - 20px)" : "700px",
                  width: "100%",
                  maxWidth: "100%",
                  margin: 0,
                  padding: 0
                }}
              ></div>
            </Box>
          </div>
        </SpaceBetween>
      </Container>
    </ContentLayout>
  );
};

export default Insights;